import type {
  NrtRecordFormState,
  NrtTestResult,
  NrtTestResultItem
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import nrtTestSampleResponse from "./testresult.json";

// Client-side today (no network call); async so a real backend call can replace it later.

// A real MAPPING_TEST backend response, checked in verbatim — single source
// of truth for both the mock check below and the test fixtures that reuse it.
export const nrtTestSampleItems =
  nrtTestSampleResponse.data as NrtTestResultItem[];

const buildMockItems = (formState: NrtRecordFormState): NrtTestResultItem[] =>
  nrtTestSampleItems.map(item => ({
    ...item,
    accountId: formState.accountId,
    providerId: formState.providerId
  }));

// Callers MUST only invoke this once validateNrtRecordForm passes (FR-014);
// a missing expression throws, caught below as a system error (FR-009).
const requireExpression = (
  fieldLabel: string,
  value: string | null
): string => {
  if (value === null || value.trim() === "") {
    throw new Error(`${fieldLabel} expression was not provided`);
  }
  return value;
};

const hasBalancedQuotes = (expression: string): boolean =>
  (expression.split("'").length - 1) % 2 === 0 &&
  (expression.split('"').length - 1) % 2 === 0;

// No rules-evaluation service exists yet, so this only checks the expression's shape.
const SELECT_MAP_OPERATOR_PATTERN = /[=<>!]=?/;

const checkExpressionSyntax = (
  fieldLabel: string,
  expression: string
): string | null => {
  if (!hasBalancedQuotes(expression)) {
    return `${fieldLabel} expression is invalid: unbalanced quotes.`;
  }
  if (!SELECT_MAP_OPERATOR_PATTERN.test(expression)) {
    return `${fieldLabel} expression is invalid: missing a comparison or assignment operator.`;
  }
  return null;
};

export const runNrtRecordTestCheck = async (
  formState: NrtRecordFormState
): Promise<NrtTestResult> => {
  const timestamp = String(Math.floor(Date.now() / 1000));

  try {
    const selectExpression = requireExpression("Select", formState.select);
    const mapExpression = requireExpression("Map", formState.map);

    const message =
      checkExpressionSyntax("Select", selectExpression) ??
      checkExpressionSyntax("Map", mapExpression);

    if (message) {
      return { status: "ERROR", timestamp, message, items: [] };
    }

    return {
      status: "COMPLETE",
      timestamp,
      message: "Record validated successfully.",
      items: buildMockItems(formState)
    };
  } catch (error) {
    // Never rejects — mapped to ERROR with a distinct system-error message (FR-009).
    const errorMessage =
      error instanceof Error ? error.message : "unknown error";
    return {
      status: "ERROR",
      timestamp,
      items: [],
      message: `Test could not complete due to a system error: ${errorMessage}`
    };
  }
};
